import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Checkbox, Col, Drawer, Form, Input, Row, Spin } from "antd";
import PropTypes from "prop-types";

import { Modals } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const Component = ({ data = {}, onUpdate, options: { permissions = [] } }) => {
	const initialState = {};
	const [state, setState] = useState(initialState);
	const { loading, submitting, visible } = state;
	const { id, name } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleClose = () => {
		navigate(-1);
	};

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values = ObjectNumbersToEnglish(values);

				const action =
					hash === `#${Modals.CREATE}`
						? API.role.create(values)
						: API.role.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						handleClose();
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						visible: true,
						loading: true,
					}));

					API.role
						.details(id)
						.then(({ data }) => {
							form.setFieldsValue(data);
						})
						.finally(() => {
							setState((prevState) => ({
								...prevState,
								loading: false,
							}));
						});
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	const footer = (
		<>
			<Button type="primary" onClick={handleSubmit} loading={submitting}>
				{Resources.confirm}
			</Button>
			<Button onClick={() => handleClose()} loading={submitting}>
				{Resources.cancel}
			</Button>
		</>
	);

	return (
		<Drawer
			className="sp-form-drawer"
			footer={footer}
			onClose={() => handleClose()}
			title={
				id
					? `${Resources.edit} ${Resources.role} "${name || "-"}"`
					: `${Resources.add} ${Resources.role}`
			}
			maskClosable={false}
			open={visible}
			width={768}
		>
			<Form layout="vertical" form={form}>
				{loading ? (
					<Spin className="sp-drawer-spin" />
				) : (
					<>
						<Form.Item
							name="name"
							label={Resources.title}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="permissions"
							label={Resources.permissions}
							rules={Rules.required}
						>
							<Checkbox.Group style={{ width: "100%" }}>
								{permissions.map(({ alias, id, groups }) => (
									<Row gutter={[16, 8]} key={id}>
										<Col xs={24}>{`${alias}:`}</Col>
										<Col xs={24}>
											{groups.map(({ alias, id, permissions }) => (
												<Row gutter={[16, 8]} key={id}>
													<Col xs={24}>
														<hr className="sp-divider-dashed" />
													</Col>
													<Col xs={24}>{alias}</Col>
													{permissions.map(({ id, alias }) => (
														<Col xs={24} sm={12} md={8} key={id}>
															<Checkbox value={id}>{alias}</Checkbox>
														</Col>
													))}
												</Row>
											))}
										</Col>
										<Col xs={24}>
											<hr className="sp-divider" />
										</Col>
									</Row>
								))}
							</Checkbox.Group>
						</Form.Item>
						<Form.Item name="name" noStyle>
							<Input hidden />
						</Form.Item>
					</>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
};

export default Component;
